import * as React from 'react';
import { observer } from 'mobx-react';
import { SurveyState } from './states/SurveyState';
import { action } from 'mobx';
import { LoaderRow } from '../../components/atoms/layout/LoaderRow';
import { NotFoundPage } from '../../../mainv2/containers/common/NotFoundPage';
import { Page } from '../../components/atoms/applications/page';
import { PageBody } from '../../components/atoms/layout/PageBody';
import { SurveyBody } from '../../components/molecules/surveys/SurveyBody';
import { SurveyHeader } from '../../components/molecules/surveys/SurveyHeader';
import { SurveySubmitButton } from '../../components/molecules/surveys/SurveySubmitButton';
import { authenticated } from '../../components/hocs/authenticated';
import './surveyShowPage.module.scss';
import { QuestionCard } from './QuestionCard';
import { SpBr } from '../../../commonv3/components/atoms/layouts/br';
import { SurveyQuestionLinkIndexView } from '../../types/surveys';

type Props = {
    match: {
        params: {
            surveyKey: string;
        };
    };
};

type QuestionErrorPaneProps = {
    errorQuestions: SurveyQuestionLinkIndexView[];
    questionIndexOffset?: number;
};

export const QuestionErrorPane = ({ errorQuestions, questionIndexOffset = 1 }: QuestionErrorPaneProps) => {
    return (
        <div id="survey-error-message" styleName="error">
            <div styleName="message">
                ご入力ありがとうございます。<br />
                下記質問番号の回答が<SpBr />
                正しく入力できていないようです。
            </div>
            <div styleName="content">
                {errorQuestions
                    .map((q) => (
                        <a
                            key={q.survey_question_id}
                            id={`question-error-${q.survey_question_id}`}
                            href={`#question-card-${q.survey_question_id}`}
                        >
                            {q.index + questionIndexOffset}
                        </a>
                    ))
                    .reduce((array, item) => {
                        if (array.length === 0) {
                            return [item];
                        } else {
                            return array.concat([' , ', item]);
                        }
                    }, [])}
            </div>
        </div>
    );
};

@authenticated
@observer
export class SurveyShowPage extends React.Component<Props> {
    pageState = new SurveyState();

    componentDidMount(): void {
        this.init();
    }

    componentDidUpdate(prevProps: Readonly<Props>): void {
        if (prevProps.match.params.surveyKey !== this.props.match.params.surveyKey) {
            this.init();
        }
    }

    @action.bound
    init() {
        const surveyKey = this.props.match.params.surveyKey;
        this.pageState.init(surveyKey);
    }

    render() {
        if (!this.pageState.initialized) {
            return <LoaderRow />;
        }
        const {
            survey,
            parameterParseError,
            questions,
            getQuestionStateFor,
            submit,
            submitState: { submitting },
            success,
            errorQuestions,
        } = this.pageState;
        if (survey.isNotFound()) {
            return <NotFoundPage entityType="アンケート" />;
        }
        if (parameterParseError) {
            return <NotFoundPage entityType="アンケート" />;
        }
        return (
            <Page>
                <PageBody styleName="page-body">
                    <SurveyHeader surveyName={survey.get().name} surveyDescription={survey.get().description} />
                    <SurveyBody>
                        {questions.map((q, index) => {
                            const questionState = getQuestionStateFor(q.survey_question_id);
                            return (
                                <QuestionCard
                                    key={q.survey_question.id}
                                    questionNumber={index + 1}
                                    totalQuestions={questions.length}
                                    question={q.survey_question}
                                    questionState={questionState}
                                />
                            );
                        })}
                        {this.pageState.hasErrors && <QuestionErrorPane errorQuestions={errorQuestions} />}
                        <SurveySubmitButton
                            id="survey-submit-button"
                            loading={submitting}
                            disabled={false}
                            onClick={submit}
                            success={success}
                        >
                            {success ? 'ありがとうございました！' : '提出する'}
                        </SurveySubmitButton>
                    </SurveyBody>
                </PageBody>
            </Page>
        );
    }
}
